import React from 'react';
import {
  ArrayCondition,
  ArrayResolution,
  convertPeriodPreset,
  DateCondition,
  Expression,
  FieldListObject,
  Filter,
  fromDateMapping,
  getEndOfDay,
  NewRelativeDate,
  PeriodBehavior,
  Preset,
  RelativeUnit,
  SIDE_MAPPING,
  StringConditionType,
  SUBNET_SIDE_MAPPING,
  toDateMapping,
} from '@fabric-ui/components';
import { setNestedValue } from '@fabric-ui/utils';
import { SxProps } from '@mui/material';
import { add } from 'date-fns';
import { OPERATORS } from './constants';

export const boxWidth = 210;
export const selectorWidth = boxWidth - 22;
export const selectFieldWidth = selectorWidth - 40;
export const ipInputSx: React.CSSProperties = { width: '166px' };
export const ipSubnetInputSx: React.CSSProperties = { width: '52px' };

export const getExpressionType = (expression: Expression) => {
  const { fieldName, ...condition } = expression.arrayCondition?.underlying || expression;
  return Object.keys(condition)[0] as ConditionType;
};

export const getExpression = ({
  fieldName,
  typeCondition = ConditionType.string,
  operator = StringConditionType.CONTAINS,
  value,
  options,
}: {
  fieldName: string;
  typeCondition?: ConditionType;
  operator: string;
  value: any;
  options?: { matchType?: ArrayResolution; underlyingCondition?: ConditionType };
}) => {
  if (typeCondition === ConditionType.array) {
    const underlyingCondition = options?.underlyingCondition ?? ConditionType.string;
    return {
      expression: {
        fieldName: '',
        arrayCondition: {
          resolution: options?.matchType ?? ArrayResolution.ANY,
          underlying: { fieldName, [underlyingCondition]: { [operator]: value } },
        },
      },
    };
  }
  return {
    expression: { fieldName, [typeCondition]: { [operator]: value } },
  } as Filter;
};

export const getEmptyExpression = (
  fieldName: string | null = null,
  typeCondition = ConditionType.string,
  defaultNewOperator?,
  field?: FieldListObject,
  isRepeated?: boolean
) => {
  const newOperator = defaultNewOperator || defaultOperator[typeCondition];
  const underlying = { fieldName, [typeCondition]: { [newOperator]: defaultValue[typeCondition]?.(field) } };
  return {
    expression:
      (field?.repeated ?? isRepeated) && typeCondition !== 'fieldCondition'
        ? { fieldName, arrayCondition: { underlying, resolution: ArrayResolution.ANY } }
        : underlying,
  } as Filter;
};

export const cleanValueFromExpression = (filter: Filter, fieldMap: Record<string, FieldListObject>): Filter => {
  if (isIngressExp(filter)) {
    return { and: { operands: [filter.and!.operands[0], cleanValueFromExpression(filter.and!.operands[1], fieldMap)] } };
  }

  if (filter.expression) {
    return {
      expression: cleanValueFromExpressionHelper(filter.expression, fieldMap),
    };
  }

  if (filter.freeText) {
    return { freeText: { stringValue: '' } };
  }

  if (filter.and?.operands?.length) {
    return cleanValueFromExpression(filter.and.operands[0], fieldMap);
  }

  if (filter.or?.operands?.length) {
    return cleanValueFromExpression(filter.or.operands[0], fieldMap);
  }

  return { expression: { stringCondition: { contains: '' } } };
};

const cleanValueFromExpressionHelper = ({ fieldName, ...condition }: Expression, fieldMap) => {
  const conditionType = Object.keys(condition)[0] as ConditionType;
  const conditionObj = condition[conditionType];
  if (conditionType === ConditionType.datetime) {
    return { fieldName, [ConditionType.datetime]: { between: defaultValue[ConditionType.datetime]() } };
  }
  if (conditionType === ConditionType.array) {
    return {
      arrayCondition: {
        ...conditionObj,
        underlying: cleanValueFromExpressionHelper((conditionObj as ArrayCondition).underlying, fieldMap),
      },
    };
  }
  if (conditionType === ConditionType.population) {
    return {
      fieldName,
      populationCondition: conditionObj,
    };
  }
  const conditionOperand = Object.keys(conditionObj!)[0];
  const operand = conditionOperand === 'in' ? 'equals' : conditionOperand;
  return {
    fieldName,
    [conditionType]: { [operand]: isNullOperator(operand) ? {} : defaultValue[conditionType]?.(fieldMap[fieldName!]) },
  };
};

export const getCondition = (operand: Filter, typeCondition = ConditionType.string, defaultNewOperator?) => {
  const cond = operand.expression?.arrayCondition?.underlying![typeCondition] || operand.expression?.[typeCondition];
  const resolution = operand.expression?.arrayCondition?.resolution || ArrayResolution.ANY;
  return `${cond ? Object.keys(cond || [])[0] : defaultNewOperator || defaultOperator[typeCondition]}${
    operand.expression?.arrayCondition ? `|${resolution}` : ''
  }`;
};

export const getEmptyFilterExpression = (fieldName: string | null = null, defaultOperator = StringConditionType.CONTAINS) => ({
  and: {
    operands: [{ expression: { fieldName, stringCondition: { [defaultOperator]: isNullOperator(defaultOperator) ? {} : '' } } }],
  },
});

export const getEmptyTimeBasedFilter = () => ({ and: { operands: [{ config: { sourceNames: [], days: 7 } }] } });

const nullOperators = ['empty', 'notEmpty', 'not_empty', 'notExists'].flatMap(op => [op, `${op}|COMPLETE`]);
export const isNullOperator = operator => nullOperators.includes(operator);

const operatorLabelsBase = {
  contains: 'Contains',
  notContains: 'Not Contains',
  starts: 'Starts With',
  ends: 'Ends With',
  [OPERATORS.equals]: 'Equals',
  notEqual: 'Not Equal',
  notIn: 'Not In',
  notExists: 'Does Not Exist',
  notEmpty: 'Is Not Empty',
  empty: 'Is Empty',
  greater: '>',
  greater_equal: '>=',
  less: '<',
  less_equal: '<=',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  equal: '=',
  between: 'Between',
  relative: 'Relative',
  ipRange: 'IP Range',
  notIpRange: 'Not IP Range',
  cidr: 'CIDR',
  notCidr: 'Not CIDR',
  [OPERATORS.in]: 'In',
  [OPERATORS.ipInSubnet]: 'IP in Subnet',
  [OPERATORS.subnetHasIp]: 'Subnet has IP',
  [OPERATORS.includes]: 'Includes',
};

export const operatorLabels = Object.entries(operatorLabelsBase).reduce(
  (obj, [key, value]) => ({
    ...obj,
    [key]: value,
    ...(isNullOperator(key) ? { [`${key}|COMPLETE`]: `${value}` } : { [`${key}|ANY`]: `${value} (Any)`, [`${key}|ALL`]: `${value} (All)` }),
  }),
  {} as typeof operatorLabelsBase
);
const ipRegex = /^([0-9]{1,3}\.){3}[0-9]{1,3}$/;
const validateIP = (ip: string) => (ipRegex.test(ip) ? '' : 'Invalid IP');
const getErrorsCIDR = str => {
  const splitStr = str.split('/');
  const errors = [validateIP(splitStr[0]), +splitStr[1] >= 0 && +splitStr[1] <= 32 ? '' : 'Invalid CIDR'];
  return errors.some(Boolean) ? errors.join('/') : '';
};
const getErrorsRange = ({ from, to }) => {
  const errors = { from: validateIP(from), to: validateIP(to) };
  return Object.values(errors).some(Boolean) ? errors : '';
};

const getErrorsNumber = ({ from, to }) => (from > to ? { from: 'Invalid Range', to: 'Invalid Range' } : '');

export type FilterOption = {
  title: string;
  value: any;
  getErrors?: (value: any) => any;
  inputOptions?: {
    separator: string;
    placeholder1: string;
    placeholder2: string;
    getValue1: (value) => string;
    getValue2: (value) => string;
    onChange1: (first, value) => string;
    onChange2: (second, value) => string;
    sx1?: SxProps;
    sx2?: SxProps;
  };
};

export const formattingRulesOperatorOptions: FilterOption[] = [
  { value: 'gt', title: operatorLabels.gt },
  { value: 'gte', title: operatorLabels.gte },
  { value: 'lt', title: operatorLabels.lt },
  { value: 'lte', title: operatorLabels.lte },
  {
    value: 'between',
    title: operatorLabels.between,
    getErrors: getErrorsNumber,
    inputOptions: {
      separator: '-',
      placeholder1: '0',
      placeholder2: '100',
      getValue1: ({ from }) => from,
      getValue2: ({ to }) => to,
      onChange1: (from, value) => ({ ...value, from }),
      onChange2: (to, value) => ({ ...value, to }),
    },
  },
];

export const supportParserOperatorOptions: Record<string, FilterOption[]> = {
  string: [
    { value: 'equals', title: operatorLabels.equals },
    { value: 'notEqual', title: operatorLabels.notEqual },
    { value: 'contains', title: operatorLabels.contains },
    { value: 'starts', title: operatorLabels.starts },
    { value: 'ends', title: operatorLabels.ends },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
    { value: 'notContains', title: operatorLabels.notContains },
  ],
};

export const filterOperatorOptions: Record<string, FilterOption[]> = Object.entries({
  string: [
    { value: 'contains', title: operatorLabels.contains },
    { value: 'notContains', title: operatorLabels.notContains },
    { value: 'starts', title: operatorLabels.starts },
    { value: 'ends', title: operatorLabels.ends },
    { value: 'equals', title: operatorLabels.equals },
    { value: 'notEqual', title: operatorLabels.notEqual },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
  ],
  bool: [
    { value: 'equals', title: operatorLabels.equals },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
  ],
  date: [
    { value: 'between', title: operatorLabels.between },
    { value: 'relative', title: operatorLabels.relative },
  ],
  datetime: [
    { value: 'between', title: operatorLabels.between },
    { value: 'relative', title: operatorLabels.relative },
  ],
  number: [
    { value: 'gt', title: operatorLabels.gt },
    { value: 'gte', title: operatorLabels.gte },
    { value: 'lt', title: operatorLabels.lt },
    { value: 'lte', title: operatorLabels.lte },
    { value: 'equals', title: operatorLabels.equal },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
  ],
  FIX: [
    { value: 'equals', title: operatorLabels.equals },
    { value: 'notEqual', title: operatorLabels.notEqual },
    { value: 'contains', title: operatorLabels.contains },
    { value: 'notContains', title: operatorLabels.notContains },
    { value: 'starts', title: operatorLabels.starts },
    { value: 'ends', title: operatorLabels.ends },
    { value: 'gt', title: operatorLabels.gt },
    { value: 'gte', title: operatorLabels.gte },
    { value: 'lt', title: operatorLabels.lt },
    { value: 'lte', title: operatorLabels.lte },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
  ],
  enum: [
    { value: 'equals', title: operatorLabels.equals },
    { value: 'notEqual', title: operatorLabels.notEqual },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
  ],
  IP: [
    { value: 'equals', title: operatorLabels.equals },
    { value: 'notEqual', title: operatorLabels.notEqual },
    // { value: 'notIn', title: operatorLabels.notIn },
    { value: 'gt', title: operatorLabels.gt },
    { value: 'gte', title: operatorLabels.gte },
    { value: 'lt', title: operatorLabels.lt },
    { value: 'lte', title: operatorLabels.lte },
    { value: 'empty', title: operatorLabels.empty },
    { value: 'notEmpty', title: operatorLabels.notEmpty },
    {
      value: 'ipRange',
      title: operatorLabels.ipRange,
      getErrors: getErrorsRange,
      inputOptions: {
        separator: '-',
        placeholder1: '10.0.0.0',
        placeholder2: '0.0.0.255',
        getValue1: ({ from }) => from,
        getValue2: ({ to }) => to,
        onChange1: (from, value) => ({ ...(typeof value === 'object' ? value : {}), from }),
        onChange2: (to, value) => ({ ...(typeof value === 'object' ? value : {}), to }),
        sx1: ipInputSx,
        sx2: ipInputSx,
      },
    },
    {
      value: 'notIpRange',
      title: operatorLabels.notIpRange,
      getErrors: getErrorsRange,
      inputOptions: {
        separator: '-',
        placeholder1: '10.0.0.0',
        placeholder2: '0.0.0.255',
        getValue1: ({ from }) => from,
        getValue2: ({ to }) => to,
        onChange1: (from, value) => ({ ...value, from }),
        onChange2: (to, value) => ({ ...value, to }),
        sx1: ipInputSx,
        sx2: ipInputSx,
      },
    },
    {
      value: 'cidr',
      title: operatorLabels.cidr,
      getErrors: getErrorsCIDR,
      inputOptions: {
        separator: '/',
        placeholder1: '10.0.0.0',
        placeholder2: '24',
        getValue1: val => val.split('/')[0],
        getValue2: val => val.split('/')[1],
        onChange1: (first, value) => `${first}/${typeof value === 'string' ? value.split('/')[1] || '' : ''}`,
        onChange2: (second, value) => `${typeof value === 'string' ? value.split('/')[0] || '' : ''}/${second}`,
        sx1: ipInputSx,
        sx2: ipSubnetInputSx,
      },
    },
    {
      value: 'notCidr',
      title: operatorLabels.notCidr,
      getErrors: getErrorsCIDR,
      inputOptions: {
        separator: '/',
        placeholder1: '10.0.0.0',
        placeholder2: '24',
        getValue1: val => val.split('/')[0],
        getValue2: val => val.split('/')[1],
        onChange1: (first, value) => `${first}/${typeof value === 'string' ? value.split('/')[1] || '' : ''}`,
        onChange2: (second, value) => `${typeof value === 'string' ? value.split('/')[0] || '' : ''}/${second}`,
        sx1: ipInputSx,
        sx2: ipSubnetInputSx,
      },
    },
  ].map(operatorDef => ({
    ...operatorDef,
    getErrors: ['empty', 'notEmpty'].includes(operatorDef.value) ? undefined : operatorDef.getErrors || validateIP,
  })),
}).reduce(
  (obj, [type, value]) => ({
    ...obj,
    [type]: value,
    [`${type}[]`]: value.flatMap(op =>
      isNullOperator(op.value)
        ? [{ ...op, value: `${op.value}|COMPLETE`, title: `${op.title}` }]
        : [
            { ...op, value: `${op.value}|ANY`, title: `${op.title} (Any)` },
            { ...op, value: `${op.value}|ALL`, title: `${op.title} (All)` },
          ]
    ),
  }),
  {}
);

export enum ConditionType {
  string = 'stringCondition',
  date = 'dateCondition',

  'historic-date' = 'dateCondition',

  datetime = 'dateCondition',
  number = 'numberCondition',
  bool = 'boolCondition',
  IP = 'ipCondition',
  FIX = 'fixCondition',
  array = 'arrayCondition',
  field = 'fieldCondition',
  enum = 'enumCondition',
  population = 'populationCondition',
}

export const booleanOptions = [
  { title: 'True', value: true },
  { title: 'False', value: false },
  { title: 'Not defined', value: null },
];
export const booleanOptions2 = [
  { title: 'True', value: true },
  { title: 'False', value: false },
];

export const defaultOperator = {
  [ConditionType.string]: 'contains',
  [ConditionType.date]: 'between',
  [ConditionType.number]: 'gt',
  [ConditionType.bool]: 'equals',
  [ConditionType.IP]: 'equals',
  [ConditionType.FIX]: 'equals',
  [ConditionType.field]: 'equals',
  [ConditionType.enum]: 'equals',
};
export const defaultValue = {
  [ConditionType.string]: () => '',
  [ConditionType.date]: () => ({ values: [new Date(), new Date()] }),
  [ConditionType.number]: () => 0,
  [ConditionType.bool]: () => true,
  [ConditionType.IP]: () => '',
  [ConditionType.FIX]: () => '',
  [ConditionType.field]: () => '',
  [ConditionType.enum]: ({ originalType }: FieldListObject) => originalType.values?.[0] || '',
};

export const defaultNumberBetween = { from: 1, to: 100 };
export const getStringConditionFromDatePicker = operand => {
  if (operand.value === null || operand.value === 'empty') {
    return { stringCondition: { empty: {} } };
  }

  if (operand.value === 'notEmpty') {
    return { stringCondition: { notEmpty: {} } };
  }

  if (!operand.value) {
    return {};
  }

  if (operand.preset) {
    return {
      dateCondition: {
        relative: {
          unit: RelativeUnit[operand.preset.period.toUpperCase()],
          value: operand.preset.count,
          periodBehaviour: operand.preset.type?.toUpperCase(),
        },
      },
    };
  }
  return {
    dateCondition: {
      between: { values: [operand.value.from, operand.value.to] },
    },
  };
};

export const getDatePickerFromQueryObject = (filterExp: Filter) => {
  // @ts-ignore
  const { dateCondition } = filterExp!.expression || Object.values(filterExp)[0]?.operands?.[0]?.expression || {};
  if (dateCondition) {
    return getDateFromFilter(dateCondition);
  }
  return undefined;
};

const getStartDate = relative => {
  if ([PeriodBehavior.last, PeriodBehavior.previous, PeriodBehavior.before].includes(relative.periodBehaviour)) {
    return fromDateMapping[relative.periodBehaviour][relative.unit](relative.value);
  }

  return new Date();
};

export const getEndDate = relative => {
  if (relative.periodBehaviour === PeriodBehavior.last) {
    return getEndOfDay();
  }

  if ([PeriodBehavior.before, PeriodBehavior.previous].includes(relative.periodBehaviour)) {
    // NewRelativeDate uses `value`; fall back to `count` (Preset shape) for legacy support
    return toDateMapping[relative.periodBehaviour][relative.unit](relative.value ?? relative.count);
  }

  return add(new Date(), { [relative.unit.toLowerCase()]: relative.value });
};

export const getDateFromFilter = (dateCondition: DateCondition) => {
  const { relative } = dateCondition;
  if (relative) {
    return {
      value: {
        from: getStartDate(relative) as Date,
        to: getEndDate(relative) as Date,
      },
      preset: {
        type: (relative as NewRelativeDate).periodBehaviour?.toLowerCase(),
        count: relative.value,
        period: convertPeriodPreset[relative.unit],
      } as Preset,
    };
  }

  if (dateCondition.between) {
    return {
      value: {
        from: dateCondition.between.values[0],
        to: dateCondition.between.values[1],
      },
    };
  }

  return {
    value: {
      from: new Date(),
      to: new Date(),
    },
  };
};

enum TransformOperator {
  greater = 'gt',
  greater_equal = 'gte',
  less = 'lt',
  less_equal = 'lte',
}
export const getOperator = operator => TransformOperator[operator] || operator;

export const getFilterOptionsWithLegacy = (options: FilterOption[], operator: string) =>
  options.find(({ value }) => value === getOperator(operator))
    ? options
    : [{ value: operator, title: `${operatorLabelsBase[operator]} (Legacy)` }, ...options];

export const getEmptyIngressSourcesFilter = () => ({ and: { operands: [getIngressExpression(getEmptyExpression())] } });

export const getIngressExpression = (innerFilter: Filter): Filter => ({ and: { operands: [getSourcesExp(null), innerFilter] } });

export const getSourcesExp = (source, projectionName = 'asset') =>
  getExpression({
    fieldName: `${projectionName}.sources.source_names`, // TODO - understand how to make it generic - use the projection | self alias or maybe use the second operand
    typeCondition: ConditionType.array,
    operator: StringConditionType.CONTAINS,
    value: source,
  });

const geIngressUberFieldName = (isUberToIngress: boolean, fieldName: string) => {
  const splittedFieldName = fieldName.split(isUberToIngress ? '.' : '.sources.');

  if (!splittedFieldName?.[1]) {
    return null;
  }
  return isUberToIngress
    ? `${splittedFieldName[0]}.sources.${splittedFieldName.slice(1).join('.')}`
    : `${splittedFieldName[0]}.${splittedFieldName.slice(1).join('.')}`;
};

export const flipFilter = ({ filter, isUberToIngress }: { filter: Filter; isUberToIngress: boolean }): Filter => {
  if (filter.expression?.arrayCondition) {
    const underlyingFieldName = filter.expression.arrayCondition.underlying.fieldName;
    if (!underlyingFieldName) {
      return filter;
    }

    const newFieldName = geIngressUberFieldName(isUberToIngress, underlyingFieldName);
    const flippedFilter = setNestedValue(filter, 'expression.arrayCondition.underlying.fieldName', newFieldName);
    return newFieldName ? flippedFilter : filter;
  }

  if (!filter.expression?.fieldName) {
    return filter;
  }

  const newFieldName = geIngressUberFieldName(isUberToIngress, filter.expression.fieldName);
  const flippedFilter = setNestedValue(filter, 'expression.fieldName', newFieldName);
  return newFieldName ? flippedFilter : filter;
};

const isIngressField = (expression?: Expression): boolean => {
  if (expression?.arrayCondition) {
    return expression.arrayCondition?.underlying.fieldName?.includes('.sources.') || false;
  }
  return expression?.fieldName?.includes('.sources.') || false;
};

export const isIngressExp = (innerFilter: Filter) =>
  !!innerFilter.and && innerFilter.and.operands.length === 2 && isIngressField(innerFilter.and.operands[0].expression);

export const getRelationalExpressionProps = ({
  operator,
  typeCondition,
  enableCaseSensitive,
  enableSide,
}: {
  operator: string;
  typeCondition: ConditionType;
  enableCaseSensitive: boolean;
  enableSide: boolean;
}) => ({
  ...(enableCaseSensitive && operator === OPERATORS.equals && typeCondition === ConditionType.field && { caseInsensitive: false }),
  ...(enableSide && SIDE_MAPPING[operator] && { side: SIDE_MAPPING[operator] }),
  ...(enableSide && SUBNET_SIDE_MAPPING[operator] && { subnetSide: SUBNET_SIDE_MAPPING[operator] }),
});

export const countExpressions = (obj: Filter) => {
  const directCount = obj.expression ? 1 : 0;

  const andCount = obj.and?.operands?.reduce((total, operand) => total + countExpressions(operand), 0) || 0;
  const orCount = obj.or?.operands?.reduce((total, operand) => total + countExpressions(operand), 0) || 0;

  return directCount + andCount + orCount;
};

export const removeMetricsFromFilter = (filter: Filter, measurementsAsObject: Record<string, any>): Filter | null => {
  if (!filter) {
    return null;
  }

  if (filter.expression) {
    const fieldName = filter.expression.fieldName || filter.expression.arrayCondition?.underlying?.fieldName;
    if (fieldName && measurementsAsObject[fieldName]) {
      return null; // This is a metric filter, remove it
    }
    return filter; // Keep non-metric expressions
  }

  if (filter.and?.operands) {
    const newOperands = filter.and.operands
      .map(operand => removeMetricsFromFilter(operand, measurementsAsObject))
      .filter(Boolean) as Filter[]; // Filter out nulls

    if (newOperands.length === 0) {
      return null;
    }
    if (newOperands.length === 1) {
      return newOperands[0];
    }
    return { and: { operands: newOperands } };
  }

  if (filter.or?.operands) {
    const newOperands = filter.or.operands
      .map(operand => removeMetricsFromFilter(operand, measurementsAsObject))
      .filter(Boolean) as Filter[]; // Filter out nulls

    if (newOperands.length === 0) {
      return null;
    }
    if (newOperands.length === 1) {
      return newOperands[0];
    }
    return { or: { operands: newOperands } };
  }

  return filter;
};

export const getUniqueFieldNames = (obj: Filter, fieldNamesSet = new Set()) => {
  if (obj.expression) {
    if (obj.expression.fieldName) {
      fieldNamesSet.add(obj.expression.fieldName);
    }

    if (obj.expression.arrayCondition?.underlying?.fieldName) {
      fieldNamesSet.add(obj.expression.arrayCondition.underlying.fieldName);
    }
  }

  if (obj.and?.operands) {
    obj.and.operands.forEach(operand => getUniqueFieldNames(operand, fieldNamesSet));
  }

  if (obj.or?.operands) {
    obj.or.operands.forEach(operand => getUniqueFieldNames(operand, fieldNamesSet));
  }

  return fieldNamesSet;
};

export const isTopKFilter = (type: string) => type === 'TOP_K';

export const rawExpressionToFilter = (field, exp): Filter => {
  const op = exp.and ? 'and' : exp.or ? 'or' : undefined;
  const fieldName = Array.isArray(field) ? field[0] : field;

  if (isTopKFilter(exp.type)) {
    return {
      expression: {
        fieldName: field,
        populationCondition: {
          name: exp.value,
          topK: {
            k: exp.top,
            dir: exp.dir,
          },
        },
      },
    };
  }

  if (!op) {
    return exp.expression;
  }

  return {
    [op]: {
      operands: exp[op].map(({ operator, value }) => ({
        expression: {
          fieldName,
          stringCondition: { [operator]: [StringConditionType.EMPTY, StringConditionType.NOT_EMPTY].includes(operator) ? {} : value },
        },
      })),
    },
  };
};

export const filterToRawExpression = (filter: Filter) => {
  const op = filter.and ? 'and' : filter.or ? 'or' : filter.expression ? 'expression' : undefined;

  const parseExpression = (expression: Expression | undefined) => {
    const [operator, value] = expression?.stringCondition ? Object.entries(expression?.stringCondition)[0] : [];
    return operator ? { operator, value } : null;
  };

  if (filter.expression?.populationCondition) {
    return {
      type: 'TOP_K',
      top: filter.expression.populationCondition.topK.k,
      dir: filter.expression.populationCondition.topK.dir,
      value: filter.expression.populationCondition.name,
    };
  }

  if (
    !op ||
    filter.expression?.stringCondition?.contains === '' ||
    (filter.and?.operands.length === 1 && filter.and.operands[0].expression?.stringCondition?.contains === '')
  ) {
    return null;
  }

  if (op === 'expression') {
    return { and: [parseExpression(filter.expression)] };
  }

  return {
    [op]: filter[op]?.operands.map(({ expression }) => parseExpression(expression)).filter(f => f),
  };
};

export const getFilterConditionType = (f: Filter): ConditionType | undefined => {
  const operator = getRootOperator(f);
  if (operator === 'expression' && f.expression?.fieldName) {
    return getExpressionType(f.expression);
  }

  if (operator === 'and' || operator === 'or') {
    const operands = (f as any)[operator]?.operands;
    if (operands?.length > 0) {
      return getFilterConditionType(operands[0]);
    }
  }

  return undefined;
};

export const shouldRenderWithFieldFilterBox = (filter: Filter, hasListTab: boolean): boolean => {
  if (hasListTab && getFilterConditionType(filter) === ConditionType.string) {
    const operator = getRootOperator(filter);

    if (operator === 'expression' || operator === 'freeText' || isIngressExp(filter)) {
      return true;
    }

    if (operator === 'and' || operator === 'or') {
      const operands = filter[operator]?.operands;

      if (!operands || operands.length === 0) {
        return true;
      }

      if (operands.some(op => !op.expression && !op.freeText)) {
        return false;
      }
      const firstFieldName = operands[0].expression?.fieldName;
      if (!firstFieldName) {
        return false;
      }

      if (operands.every(op => op.expression?.fieldName === firstFieldName)) {
        return true;
      }
    }

    return false;
  }
  return getRootOperator(filter) === 'expression' || getRootOperator(filter) === 'freeText' || isIngressExp(filter);
};

export const getRootOperator = <T extends string = string>(filter: Record<string, any>): T => {
  return Object.keys(filter).filter(key => key !== '_metadata')[0] as T;
};
